import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import ImageDesigner from '../ImageDesigner';
import VideoDesigner from '../VideoDesigner';
import './MediaGalleryDesigner.css';
import {
  Image as ImageIcon,
  Videocam as VideoIcon,
  AddPhotoAlternate as AddPhotoIcon,
  VideoCall as AddVideoIcon,
  Edit as EditIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import StrictModeDroppable from '../StrictModeDroppable';
import {
  getMediaDisplayUrl,
  normalizePhotoFromApi,
  normalizeVideoFromApi,
} from '../../utils/mediaUtils';
import VideoThumbnail from '../VideoThumbnail';

const MediaGalleryDesigner = forwardRef(({
  images = [],
  videos = [],
  maxImages = 5,
  maxVideos = 2,
  onImagesChange,
  onVideosChange,
  onViewModeChange,
}, ref) => {
  const [photos, setPhotos] = useState(images);
  const [videosList, setVideosList] = useState(videos);
  const [selectedMediaType, setSelectedMediaType] = useState(null); // 'image' ou 'video'
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [viewMode, setViewMode] = useState('gallery'); // 'gallery' ou 'detail'

  // Références pour suivre les mises à jour internes vs externes
  const isInternalImageUpdate = useRef(false);
  const isInternalVideoUpdate = useRef(false);

  const handleBackToGallery = useCallback(() => {
    setViewMode('gallery');
    setSelectedMediaType(null);
    setSelectedIndex(0);
  }, []);

  useImperativeHandle(ref, () => ({
    backToGallery: handleBackToGallery,
  }), [handleBackToGallery]);

  useEffect(() => {
    onViewModeChange?.(viewMode);
  }, [viewMode, onViewModeChange]);

  // Améliorer la gestion des images existantes
  useEffect(() => {
    if (!isInternalImageUpdate.current) {
      const formattedImages = images.map((img) => {
        if (!img) return img;
        if (img.id || img.url || img.file) return normalizePhotoFromApi(img);
        if (typeof img === 'string') {
          return normalizePhotoFromApi({
            url: img,
            alt: 'Image produit',
          });
        }
        return img;
      });
      setPhotos(formattedImages);
    } else {
      isInternalImageUpdate.current = false;
    }
  }, [images]);

  // Améliorer la gestion des vidéos existantes
  useEffect(() => {
    if (!isInternalVideoUpdate.current) {
      const formattedVideos = videos.map((vid) => {
        if (!vid) return vid;
        if (vid.id || vid.src || vid.url || vid.file) return normalizeVideoFromApi(vid);
        if (typeof vid === 'string') {
          return normalizeVideoFromApi({
            src: vid,
            alt: 'Vidéo produit',
          });
        }
        return vid;
      });
      setVideosList(formattedVideos);
    } else {
      isInternalVideoUpdate.current = false;
    }
  }, [videos]);

  const handleImageChange = (index, newImage) => {
    let updatedPhotos;

    if (index === photos.length) {
      if (newImage) {
        updatedPhotos = [...photos, newImage];
      }
    } else if (newImage) {
      updatedPhotos = photos.map((photo, i) => (i === index ? newImage : photo));
    } else {
      updatedPhotos = photos.filter((_, i) => i !== index);
      // Ajuster l'index sélectionné si nécessaire
      if (selectedIndex >= updatedPhotos.length && updatedPhotos.length > 0) {
        setSelectedIndex(updatedPhotos.length - 1);
      } else if (updatedPhotos.length === 0) {
        setViewMode('gallery');
      }
    }

    isInternalImageUpdate.current = true;
    setPhotos(updatedPhotos);
    onImagesChange(updatedPhotos);
  };

  const handleVideoChange = (index, newVideo) => {
    let updatedVideos;

    if (index === videosList.length) {
      if (newVideo) {
        updatedVideos = [...videosList, newVideo];
      } else {
        return;
      }
    } else if (newVideo) {
      updatedVideos = videosList.map((video, i) => (i === index ? newVideo : video));
    } else {
      updatedVideos = videosList.filter((_, i) => i !== index);
      // Ajuster l'index sélectionné si nécessaire
      if (selectedIndex >= updatedVideos.length && updatedVideos.length > 0) {
        setSelectedIndex(updatedVideos.length - 1);
      } else if (updatedVideos.length === 0) {
        setViewMode('gallery');
      }
    }

    isInternalVideoUpdate.current = true;
    setVideosList(updatedVideos);
    onVideosChange(updatedVideos);
  };

  // Gestion du drag and drop
  const handleDragEnd = (result) => {
    const { source, destination } = result;

    // Si l'élément est déposé en dehors d'une zone de dépôt
    if (!destination) {
      return;
    }

    // Si l'élément est déposé au même endroit
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    // Réorganisation des images
    if (source.droppableId === 'images-gallery' || source.droppableId === 'images-thumbnails') {
      const newPhotos = Array.from(photos);
      const [movedItem] = newPhotos.splice(source.index, 1);
      newPhotos.splice(destination.index, 0, movedItem);

      // Ajuster l'index sélectionné si on est en vue détaillée
      if (viewMode === 'detail' && selectedMediaType === 'image') {
        if (source.index === selectedIndex) {
          setSelectedIndex(destination.index);
        } else if (source.index < selectedIndex && destination.index >= selectedIndex) {
          setSelectedIndex(selectedIndex - 1);
        } else if (source.index > selectedIndex && destination.index <= selectedIndex) {
          setSelectedIndex(selectedIndex + 1);
        }
      }

      isInternalImageUpdate.current = true;
      setPhotos(newPhotos);
      onImagesChange(newPhotos);
    }

    // Réorganisation des vidéos
    if (source.droppableId === 'videos-gallery' || source.droppableId === 'videos-thumbnails') {
      const newVideos = Array.from(videosList);
      const [movedItem] = newVideos.splice(source.index, 1);
      newVideos.splice(destination.index, 0, movedItem);

      // Ajuster l'index sélectionné si on est en vue détaillée
      if (viewMode === 'detail' && selectedMediaType === 'video') {
        if (source.index === selectedIndex) {
          setSelectedIndex(destination.index);
        } else if (source.index < selectedIndex && destination.index >= selectedIndex) {
          setSelectedIndex(selectedIndex - 1);
        } else if (source.index > selectedIndex && destination.index <= selectedIndex) {
          setSelectedIndex(selectedIndex + 1);
        }
      }

      isInternalVideoUpdate.current = true;
      setVideosList(newVideos);
      onVideosChange(newVideos);
    }
  };

  const handleMediaClick = (type, index) => {
    setSelectedMediaType(type);
    setSelectedIndex(index);
    setViewMode('detail');
  };

  const handleThumbnailClick = (index) => {
    setSelectedIndex(index);
  };

  // Vue galerie (aperçu avec miniatures)
  if (viewMode === 'gallery') {
    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="media-gallery-container">
          {/* Section des images */}
          <div className="media-section">
            <div className="section-header">
              <ImageIcon className="section-icon" />
              <h2>Images ({photos.length}/{maxImages})</h2>
            </div>

            <StrictModeDroppable droppableId="images-gallery" direction="horizontal">
              {(provided) => (
                <div className="media-grid" ref={provided.innerRef} {...provided.droppableProps}>
                  {photos.map((photo, index) => (
                    <Draggable
                      key={photo.id || `image-${index}`}
                      draggableId={photo.id?.toString() || `image-${index}`}
                      index={index}
                    >
                      {(providedDraggable, snapshot) => (
                        <div
                          ref={providedDraggable.innerRef}
                          {...providedDraggable.draggableProps}
                          className={`media-thumbnail ${snapshot.isDragging ? 'dragging' : ''}`}
                          onClick={() => !snapshot.isDragging && handleMediaClick('image', index)}
                        >
                          <div
                            {...providedDraggable.dragHandleProps}
                            className="drag-handle"
                            title="Glisser pour réorganiser"
                          >
                            <DragIcon />
                          </div>
                          <div className="thumbnail-overlay">
                            <EditIcon className="edit-icon" />
                          </div>
                          <img
                            src={getMediaDisplayUrl(photo)}
                            alt={photo.alt || 'Image produit'}
                            className="thumbnail-image"
                            loading="lazy"
                            decoding="async"
                          />
                          <div className="thumbnail-order">{index + 1}</div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}

                  {/* Bouton d'ajout d'image */}
                  {photos.length < maxImages && (
                    <div
                      className="media-add-button"
                      onClick={() => handleMediaClick('image', photos.length)}
                    >
                      <AddPhotoIcon className="add-icon" />
                      <span>Ajouter une image</span>
                    </div>
                  )}
                </div>
              )}
            </StrictModeDroppable>
          </div>

          {/* Section des vidéos */}
          <div className="media-section">
            <div className="section-header">
              <VideoIcon className="section-icon" />
              <h2>Vidéos ({videosList.length}/{maxVideos})</h2>
            </div>

            <StrictModeDroppable droppableId="videos-gallery" direction="horizontal">
              {(provided) => (
                <div className="media-grid" ref={provided.innerRef} {...provided.droppableProps}>
                  {videosList.map((video, index) => (
                    <Draggable
                      key={video.id || `video-${index}`}
                      draggableId={video.id?.toString() || `video-${index}`}
                      index={index}
                    >
                      {(providedDraggable, snapshot) => (
                        <div
                          ref={providedDraggable.innerRef}
                          {...providedDraggable.draggableProps}
                          className={`media-thumbnail ${snapshot.isDragging ? 'dragging' : ''}`}
                          onClick={() => !snapshot.isDragging && handleMediaClick('video', index)}
                        >
                          <div
                            {...providedDraggable.dragHandleProps}
                            className="drag-handle"
                            title="Glisser pour réorganiser"
                          >
                            <DragIcon />
                          </div>
                          <div className="thumbnail-overlay">
                            <EditIcon className="edit-icon" />
                          </div>
                          <VideoThumbnail
                            src={getMediaDisplayUrl(video)}
                            className="thumbnail-video"
                          />
                          <div className="thumbnail-order">{index + 1}</div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}

                  {/* Bouton d'ajout de vidéo */}
                  {videosList.length < maxVideos && (
                    <div
                      className="media-add-button"
                      onClick={() => handleMediaClick('video', videosList.length)}
                    >
                      <AddVideoIcon className="add-icon" />
                      <span>Ajouter une vidéo</span>
                    </div>
                  )}
                </div>
              )}
            </StrictModeDroppable>
          </div>
        </div>
      </DragDropContext>
    );
  }

  // Vue détaillée (édition d'un média spécifique)
  const currentList = selectedMediaType === 'image' ? photos : videosList;
  const currentMedia = currentList[selectedIndex];

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="media-detail-container">
        <div className="detail-content">
          <div className="detail-editor">
            {selectedMediaType === 'image' ? (
              <ImageDesigner
                key={`image-${selectedIndex}`}
                image={currentMedia}
                onImageChange={(newImage) => handleImageChange(selectedIndex, newImage)}
                showActionsBelow={false}
              />
            ) : (
              <VideoDesigner
                key={`video-${selectedIndex}`}
                video={currentMedia}
                onVideoChange={(newVideo) => handleVideoChange(selectedIndex, newVideo)}
              />
            )}
          </div>

          <div className="detail-thumbnails">
            <StrictModeDroppable
              droppableId={selectedMediaType === 'image' ? 'images-thumbnails' : 'videos-thumbnails'}
              direction="horizontal"
            >
              {(provided) => (
                <div className="thumbnails-container" ref={provided.innerRef} {...provided.droppableProps}>
                  {currentList.map((media, index) => (
                    <Draggable
                      key={index}
                      draggableId={`${selectedMediaType}-thumb-${index}`}
                      index={index}
                    >
                      {(providedDraggable, snapshot) => (
                        <div
                          ref={providedDraggable.innerRef}
                          {...providedDraggable.draggableProps}
                          className={`detail-thumbnail ${index === selectedIndex ? 'active' : ''} ${snapshot.isDragging ? 'dragging' : ''}`}
                          onClick={() => !snapshot.isDragging && handleThumbnailClick(index)}
                        >
                          <div
                            {...providedDraggable.dragHandleProps}
                            className="thumbnail-drag-handle"
                            title="Glisser pour réorganiser"
                          >
                            <DragIcon fontSize="small" />
                          </div>
                          {selectedMediaType === 'image' ? (
                            <img
                              src={getMediaDisplayUrl(media)}
                              alt={media.alt || 'Image'}
                              className="thumbnail-image-small"
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <VideoThumbnail
                              src={getMediaDisplayUrl(media)}
                              className="thumbnail-video-small"
                            />
                          )}
                          <div className="thumbnail-index">{index + 1}</div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </StrictModeDroppable>
          </div>
        </div>
      </div>
    </DragDropContext>
  );
});

MediaGalleryDesigner.displayName = 'MediaGalleryDesigner';

export default MediaGalleryDesigner;
